/**
 * useSounds — саунд-дизайн Luna онбоардинга.
 *
 * Версия 9 (18 Apr 2026): минимальный HTMLAudio + пул, без unlock-логики.
 * Та же схема что и в useUISounds — всё синхронно в user-gesture,
 * iOS WebView разрешает first-play без прогрева.
 *
 * MP3 физически зафадены через ffmpeg (5% in/out) — щелчков нет.
 */
import { useCallback, useRef } from 'react';

type SoundName =
  | 'tap'
  | 'swipe'
  | 'success'
  | 'milestone'
  | 'coin'
  | 'shimmer'
  | 'sandboxEarning';

const SOUND_FILES: Record<SoundName, string> = {
  tap: '/onboarding/sounds/tap.mp3',
  swipe: '/onboarding/sounds/swipe.mp3',
  success: '/onboarding/sounds/success.mp3',
  milestone: '/onboarding/sounds/milestone.mp3',
  coin: '/onboarding/sounds/coin.mp3',
  shimmer: '/onboarding/sounds/shimmer.mp3',
  sandboxEarning: '/onboarding/sounds/sandbox_earning_v2.mp3',
};

const VOLUME_DEFAULT = 0.55 * 0.7; // ≈0.385
const VOLUME_QUIET = 0.45 * 0.7; // ≈0.315

function getPeak(name: SoundName): number {
  if (name === 'milestone' || name === 'sandboxEarning') return VOLUME_QUIET;
  return VOLUME_DEFAULT;
}

// POOL_SIZE=1, lazy-создание — см. комментарий в useUISounds.ts
const POOL_SIZE = 1;

interface Pool {
  audios: HTMLAudioElement[];
  cursor: number;
}
const pools: Partial<Record<SoundName, Pool>> = {};

function ensurePool(name: SoundName): Pool | null {
  if (typeof window === 'undefined') return null;
  const existing = pools[name];
  if (existing) return existing;
  const audios: HTMLAudioElement[] = [];
  for (let i = 0; i < POOL_SIZE; i++) {
    const a = new Audio(SOUND_FILES[name]);
    a.preload = 'auto';
    a.volume = getPeak(name);
    a.addEventListener('ended', () => { a.currentTime = 0; });
    audios.push(a);
  }
  const p: Pool = { audios, cursor: 0 };
  pools[name] = p;
  return p;
}

function play(name: SoundName): () => void {
  const pool = ensurePool(name);
  if (!pool || pool.audios.length === 0) return () => {};
  const a = pool.audios[pool.cursor]!;
  pool.cursor = (pool.cursor + 1) % pool.audios.length;
  try {
    if (!a.paused) a.pause();
    a.currentTime = 0;
    a.volume = getPeak(name);
    const p = a.play();
    if (p && typeof p.then === 'function') void p.catch(() => {});
  } catch {
    // silent
  }
  return () => {
    try { a.pause(); a.currentTime = 0; } catch { /* silent */ }
  };
}

export function useSounds() {
  const lastTapRef = useRef(0);
  // Ничего не создаём в useEffect — пулы lazy-создаются при первом play()

  const tap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 30) return;
    lastTapRef.current = now;
    play('tap');
  }, []);

  const swipe = useCallback(() => play('swipe'), []);
  const success = useCallback(() => play('success'), []);
  const milestone = useCallback(() => play('milestone'), []);
  const coin = useCallback(() => play('coin'), []);
  const shimmer = useCallback(() => play('shimmer'), []);
  const sandboxEarning = useCallback((): (() => void) => play('sandboxEarning'), []);

  return { tap, swipe, success, milestone, coin, shimmer, sandboxEarning };
}
